import {
  CreateWorkflowFilteringPayload,
  DeleteWorkflowFilteringPayload,
  UpdateWorkflowFilteringPayload,
} from '@/pages/FundLevelAdmin/types';
import { AxiosInstance } from 'axios';

export default class FundLevelAdminAPI {
  client: AxiosInstance;
  constructor(client: AxiosInstance) {
    this.client = client;
  }

  //  -------------------------------
  //  Fund Module APIS  / V2 Fund Level admin Page
  //  -------------------------------

  async getClassificationTagsBankRelationships() {
    return this.client.get(
      'v2/fund-level-hedging/get-classification-tags-bank-relationships/',
    );
  }
  async getBankFundCompanyPlatformMapping() {
    return this.client.get(
      'v2/fund-level-hedging/get-bank-fund-company-platform-mapping/',
    );
  }
  async getAllTransactionPlatforms(payload?: any) {
    return this.client.get(
      'v2/fund-level-hedging/get-all-transaction-platforms/',
      { params: payload },
    );
  }

  async getAllFundCompanies() {
    return this.client.get('v2/fund-level-hedging/unfiltered-fund-companies/');
  }

  async createFundCompany(payload: any) {
    return this.client.post(
      'v2/fund-level-hedging/create-fund-company/',
      payload,
    );
  }

  async createClassificationTagBankRelationship(payload: any) {
    return this.client.post(
      'v2/fund-level-hedging/create-classification-tag-bank-relationship/',
      payload,
    );
  }

  async createBankFundCompanyPlatformMapping(payload: any) {
    return this.client.post(
      'v2/fund-level-hedging/create-bank-fund-company-platform-mapping/',
      payload,
    );
  }

  async updateClassificationTagBankRelationship(payload: any) {
    return this.client.post(
      'v2/fund-level-hedging/update-classification-tag-bank-relationship/',
      payload,
    );
  }

  async updateBankFundCompanyPlatformMapping(payload: any) {
    return this.client.post(
      'v2/fund-level-hedging/update-bank-fund-company-platform-mapping/',
      payload,
    );
  }
  async updateFundCompany(payload: any) {
    return this.client.post(
      'v2/fund-level-hedging/update-fund-company/',
      payload,
    );
  }

  async deleteClassificationTagBankRelationship(id: number) {
    return this.client.post(
      `v2/fund-level-hedging/delete-classification-tag-bank-relationship/`,
      { data: { id } },
    );
  }

  async deleteBankFundCompanyPlatformMapping(id: number) {
    return this.client.post(
      `v2/fund-level-hedging/delete-bank-fund-company-platform-mapping/`,
      { data: { id } },
    );
  }

  //  -------------------------------
  //  User Initiator APIs
  //  -------------------------------

  async getUserInitiators(params?: {
    activeOnly?: boolean;
    fundCompanyId?: number;
  }) {
    return this.client.get('v2/user/user-initiator/', { params });
  }

  async getUserInitiatorById(id: number) {
    return this.client.get(`v2/user/user-initiator/${id}/`);
  }

  async createUserInitiator(payload: any) {
    return this.client.post('v2/user/user-initiator/', payload);
  }

  async updateUserInitiator(id: number, payload: any) {
    return this.client.put(`v2/user/user-initiator/${id}/`, payload);
  }

  async deleteUserInitiator(id: number) {
    return this.client.delete(`v2/user/user-initiator/${id}/`);
  }

  //  -------------------------------
  //  User Authorization APIs
  //  -------------------------------

  async getUserAuthorizations(params?: {
    activeOnly?: boolean;
    fundCompany?: number;
  }) {
    return this.client.get('v2/user/user-authorization/', { params });
  }

  async getUserAuthorizationById(id: number) {
    return this.client.get(`v2/user/user-authorization/${id}/`);
  }

  async createUserAuthorization(payload: any) {
    return this.client.post('v2/user/user-authorization/', payload);
  }

  async updateUserAuthorization(id: number, payload: any) {
    return this.client.put(`v2/user/user-authorization/${id}/`, payload);
  }

  async deleteUserAuthorization(id: number) {
    return this.client.delete(`v2/user/user-authorization/${id}/`);
  }

  //  -------------------------------
  //  Fund Class Tag Email APIs
  //  -------------------------------

  async getFundClassTagEmails(params?: {
    classificationTag?: string;
    grouped?: boolean;
  }) {
    return this.client.get('v2/fund-level-hedging/fund-class-tag-emails/', {
      params,
    });
  }

  async getFundClassTagEmailById(id: number) {
    return this.client.get(
      `v2/fund-level-hedging/fund-class-tag-emails/${id}/`,
    );
  }

  async createFundClassTagEmail(payload: any) {
    return this.client.post(
      'v2/fund-level-hedging/fund-class-tag-emails/',
      payload,
    );
  }

  async updateFundClassTagEmail(id: number, payload: any) {
    return this.client.put(
      `v2/fund-level-hedging/fund-class-tag-emails/${id}/`,
      payload,
    );
  }

  async deleteFundClassTagEmail(id: number) {
    return this.client.delete(
      `v2/fund-level-hedging/fund-class-tag-emails/${id}/`,
    );
  }

  async bulkCreateFundClassTagEmails(payload: {
    classification_tag: string;
    email_addresses: string[];
  }) {
    return this.client.post(
      'v2/fund-level-hedging/fund-class-tag-emails/bulk/',
      payload,
    );
  }

  async bulkDeleteFundClassTagEmails(payload: { ids: number[] }) {
    return this.client.delete(
      'v2/fund-level-hedging/fund-class-tag-emails/bulk/',
      { data: payload },
    );
  }

  async mapEmailToMultipleTags(payload: {
    email_address: string;
    classification_tags: string[];
  }) {
    return this.client.post(
      'v2/fund-level-hedging/fund-class-tag-emails/email-to-tags/',
      payload,
    );
  }

  //  -------------------------------
  //  Classification Tag Legal Entity APIs
  //  -------------------------------

  async getClassificationTagLegalEntities(params?: {
    classificationTag?: string;
    activeOnly?: boolean;
  }) {
    return this.client.get('v2/hedging/classification-tag-legal-entities/', {
      params,
    });
  }

  async getClassificationTagLegalEntityById(id: number) {
    return this.client.get(
      `v2/hedging/classification-tag-legal-entities/${id}/`,
    );
  }

  async createClassificationTagLegalEntity(payload: any) {
    return this.client.post(
      'v2/hedging/classification-tag-legal-entities/',
      payload,
    );
  }

  async updateClassificationTagLegalEntity(id: number, payload: any) {
    return this.client.put(
      `v2/hedging/classification-tag-legal-entities/${id}/`,
      payload,
    );
  }

  async deleteClassificationTagLegalEntity(id: number) {
    return this.client.delete(
      `v2/hedging/classification-tag-legal-entities/${id}/`,
    );
  }

  //  -------------------------------
  //  Classification Tag Deal Name Hedge APIs
  //  -------------------------------

  async getClassificationTagDealNameHedges(params?: {
    classificationTag?: string;
    legalEntity?: string;
    activeOnly?: boolean;
  }) {
    return this.client.get('v2/hedging/classification-tag-deal-name-hedges/', {
      params,
    });
  }

  async getClassificationTagDealNameHedgeById(id: number) {
    return this.client.get(
      `v2/hedging/classification-tag-deal-name-hedges/${id}/`,
    );
  }

  async createClassificationTagDealNameHedge(payload: any) {
    return this.client.post(
      'v2/hedging/classification-tag-deal-name-hedges/',
      payload,
    );
  }

  async updateClassificationTagDealNameHedge(id: number, payload: any) {
    return this.client.put(
      `v2/hedging/classification-tag-deal-name-hedges/${id}/`,
      payload,
    );
  }

  async deleteClassificationTagDealNameHedge(id: number) {
    return this.client.delete(
      `v2/hedging/classification-tag-deal-name-hedges/${id}/`,
    );
  }

  //  -------------------------------
  //  Fund Level Trade Pricing APIs
  //  -------------------------------

  async getFundLevelTradePricings(params?: {
    fund?: number;
    activeOnly?: boolean;
  }) {
    return this.client.get('v2/invoice-center/fund-level-trade-pricing/', {
      params,
    });
  }

  async getFundLevelTradePricingById(id: number) {
    return this.client.get(`v2/invoice-center/fund-level-trade-pricing/${id}/`);
  }

  async createFundLevelTradePricing(payload: any) {
    return this.client.post(
      'v2/invoice-center/fund-level-trade-pricing/',
      payload,
    );
  }

  async updateFundLevelTradePricing(id: number, payload: any) {
    return this.client.put(
      `v2/invoice-center/fund-level-trade-pricing/${id}/`,
      payload,
    );
  }

  async deleteFundLevelTradePricing(id: number) {
    return this.client.delete(
      `v2/invoice-center/fund-level-trade-pricing/${id}/`,
    );
  }

  // --------------------------
  // Workflow Filtering
  // --------------------------
  async getWorkflowCompanyFilters() {
    return this.client.get(`/v2/fund-level-hedging/workflow-company-filters/`);
  }

  async createWorkflowCompanyFilter(payload: CreateWorkflowFilteringPayload) {
    return this.client.post(
      `/v2/fund-level-hedging/create-workflow-company-filter/`,
      payload,
    );
  }
  async updateWorkflowCompanyFilter(payload: UpdateWorkflowFilteringPayload) {
    return this.client.post(
      `/v2/fund-level-hedging/update-workflow-company-filter/`,
      payload,
    );
  }

  async deleteWorkflowCompanyFilter(payload: DeleteWorkflowFilteringPayload) {
    return this.client.post(
      `/v2/fund-level-hedging/delete-workflow-company-filter/`,
      payload,
    );
  }

  //  -------------------------------
  //  Bid Criteria APIs
  //  (FundCompanyBankBidCriteria)
  //  -------------------------------

  async getBidCriteria(params?: {
    fund_company?: number;
    bid?: boolean;
    execution?: boolean;
  }) {
    return this.client.get(
      'v2/fund-level-hedging/fund-company-bk-bid-criteria/',
      { params },
    );
  }

  async getBidCriteriaById(id: number) {
    return this.client.get(
      `v2/fund-level-hedging/fund-company-bk-bid-criteria/${id}/`,
    );
  }

  async createBidCriteria(payload: any) {
    return this.client.post(
      'v2/fund-level-hedging/fund-company-bk-bid-criteria/',
      payload,
    );
  }

  async updateBidCriteria(id: number, payload: any) {
    return this.client.put(
      `v2/fund-level-hedging/fund-company-bk-bid-criteria/${id}/`,
      payload,
    );
  }

  async deleteBidCriteria(id: number) {
    return this.client.delete(
      `v2/fund-level-hedging/fund-company-bk-bid-criteria/${id}/`,
    );
  }

  //  -------------------------------
  //  Execution Ledger APIs
  //  (TradeRequestBidExecutionLedger)
  //  -------------------------------

  async getExecutionLedger(params?: {
    transaction?: number;
    bid_reason?: number;
    execution_reason?: number;
  }) {
    return this.client.get(
      'v2/fund-level-hedging/trade-request-bid-execution-ledger/',
      { params },
    );
  }

  async getExecutionLedgerById(id: number) {
    return this.client.get(
      `v2/fund-level-hedging/trade-request-bid-execution-ledger/${id}/`,
    );
  }

  async createExecutionLedger(payload: any) {
    return this.client.post(
      'v2/fund-level-hedging/trade-request-bid-execution-ledger/',
      payload,
    );
  }

  async updateExecutionLedger(id: number, payload: any) {
    return this.client.put(
      `v2/fund-level-hedging/trade-request-bid-execution-ledger/${id}/`,
      payload,
    );
  }

  async deleteExecutionLedger(id: number) {
    return this.client.delete(
      `v2/fund-level-hedging/trade-request-bid-execution-ledger/${id}/`,
    );
  }

  //  -------------------------------
  //  FIX Session APIs
  //  (FixSessionDetails)
  //  -------------------------------

  async getFixSessions(params?: {
    session_name?: string;
    sender_company?: string;
    target_company?: string;
  }) {
    return this.client.get('v2/fund-level-hedging/fix-session-details/', {
      params,
    });
  }

  async getFixSessionById(id: number) {
    return this.client.get(
      `v2/fund-level-hedging/fix-session-details/${id}/`,
    );
  }

  async createFixSession(payload: any) {
    return this.client.post(
      'v2/fund-level-hedging/fix-session-details/',
      payload,
    );
  }

  async updateFixSession(id: number, payload: any) {
    return this.client.put(
      `v2/fund-level-hedging/fix-session-details/${id}/`,
      payload,
    );
  }

  async deleteFixSession(id: number) {
    return this.client.delete(
      `v2/fund-level-hedging/fix-session-details/${id}/`,
    );
  }

  //  -------------------------------
  //  FIX Reference APIs
  //  (FixReferences)
  //  -------------------------------

  async getFixReferences(params?: { reference_name?: string }) {
    return this.client.get('v2/fund-level-hedging/fix-references/', {
      params,
    });
  }

  async getFixReferenceById(id: number) {
    return this.client.get(`v2/fund-level-hedging/fix-references/${id}/`);
  }

  async createFixReference(payload: any) {
    return this.client.post(
      'v2/fund-level-hedging/fix-references/',
      payload,
    );
  }

  async updateFixReference(id: number, payload: any) {
    return this.client.put(
      `v2/fund-level-hedging/fix-references/${id}/`,
      payload,
    );
  }

  async deleteFixReference(id: number) {
    return this.client.delete(
      `v2/fund-level-hedging/fix-references/${id}/`,
    );
  }

  //  -------------------------------
  //  Trade Request Email Recipient APIs
  //  (/v2/fund-level-hedging/trade-request-email-recipients/)
  //  -------------------------------

  async getTradeRequestEmailRecipients(params?: {
    fundCompany?: number;
    classificationTag?: number;
    emailOverride?: string;
    user?: string;
  }) {
    return this.client.get(
      'v2/fund-level-hedging/trade-request-email-recipients/',
      { params },
    );
  }

  async getTradeRequestEmailRecipientById(id: number) {
    return this.client.get(
      `v2/fund-level-hedging/trade-request-email-recipients/${id}/`,
    );
  }

  async createTradeRequestEmailRecipient(payload: any) {
    return this.client.post(
      'v2/fund-level-hedging/trade-request-email-recipients/',
      payload,
    );
  }

  async updateTradeRequestEmailRecipient(id: number, payload: any) {
    return this.client.put(
      `v2/fund-level-hedging/trade-request-email-recipients/${id}/`,
      payload,
    );
  }

  async deleteTradeRequestEmailRecipient(id: number) {
    return this.client.delete(
      `v2/fund-level-hedging/trade-request-email-recipients/${id}/`,
    );
  }

  async bulkCreateTradeRequestEmailRecipients(payload: any) {
    return this.client.post(
      'v2/fund-level-hedging/trade-request-email-recipients/bulk/',
      payload,
    );
  }

  async bulkDeleteTradeRequestEmailRecipients(payload: { ids: number[] }) {
    return this.client.delete(
      'v2/fund-level-hedging/trade-request-email-recipients/bulk/',
      { data: payload },
    );
  }
}
